import { useEffect, useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { RefereeMemberSearch, formatMainAppUser } from '@/components/review/MainAppUserPicker';
import type { MainAppUser } from '@/types';

interface Props {
  open: boolean;
  role: 'manager' | 'participant';
  excludeIds: number[];
  onClose: () => void;
  onSubmit: (users: MainAppUser[]) => Promise<void>;
}

const ROLE_LABELS = {
  manager: '管理員',
  participant: '參與者',
} as const;

export default function AddGroupMembersDialog({ open, role, excludeIds, onClose, onSubmit }: Props) {
  const [selected, setSelected] = useState<MainAppUser[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setSelected([]);
  }, [open, role]);

  const handleSave = async () => {
    if (selected.length === 0) return;
    setSaving(true);
    try {
      await onSubmit(selected);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>新增{ROLE_LABELS[role]}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          搜尋並選擇裁判加入群組（可多選）。系統管理員與開發者不顯示於成員名單。
        </Typography>
        {selected.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            {selected.map((u) => (
              <Chip
                key={u.id}
                size="small"
                label={formatMainAppUser(u)}
                onDelete={() => setSelected((prev) => prev.filter((x) => x.id !== u.id))}
              />
            ))}
          </Stack>
        )}
        <RefereeMemberSearch
          selected={selected}
          onSelectedChange={setSelected}
          excludeIds={[...excludeIds, ...selected.map((u) => u.id)]}
          refereesOnly
          addLabel="加入"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={selected.length === 0 || saving}
          onClick={handleSave}
        >
          {selected.length > 1 ? `加入 ${selected.length} 人` : '加入'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
